import React, { useEffect, useRef, useState } from 'react';
import { NSSpace, NSSpaceSizeType, NSButtonBlue, IBackgroundProps, NSLoading, ProductCacheService, NSBoxBoolean, NSBox } from 'namirasoft-site-react';
import { ProductFullRow } from 'namirasoft-api-product';
import { NSARouterMakerProps } from '../NSARouterMakerProps';
import { ConsentCacheService } from '../ConsentCacheService';
import { ConsentRow } from 'namirasoft-account';
import Styles from './NSAConsentPage.module.css';
import { VersionOperation } from 'namirasoft-core';

export interface NSAConsentPageProps extends NSARouterMakerProps
{
	custom?: {
		name?: string;
		logo?: string;
		background?: IBackgroundProps;
	};
	onAccepted: () => React.JSX.Element;
}
export interface NSAConsentPageState
{
	product: ProductFullRow | null;
	consent: ConsentRow | null;
	accepted: boolean;
}

export function NSAConsentPage(props: NSAConsentPageProps) 
{
	const NSBoxBoolean_Agree = useRef<NSBoxBoolean>(null);

	let cache_product = ProductCacheService.get(props.product_id, console.error);
	let cache_consent = ConsentCacheService.get(props.account.token_manager, props.product_id, console.error);
	let [state, setState] = useState<NSAConsentPageState>({ product: null, consent: null, accepted: false });

	useEffect(() =>
	{
		cache_product.get().then(product =>
		{
			setState(prev => { return { ...prev, product } });
			cache_consent.get().then(consent =>
			{
				setState(prev => { return { ...prev, consent } });
			}).catch(() => { });
		}).catch(() => { });
		// eslint-disable-next-line 
	}, []);

	if (!state.product)
		return <NSLoading />;

	if (state.consent)
	{
		let terms = true;
		let privacy = true;
		if (!VersionOperation.isGreaterThanOrEqual(state.consent.terms_version ?? "0.0.0", state.product.terms_version ?? "0.0.0"))
			terms = false;
		if (!VersionOperation.isGreaterThanOrEqual(state.consent.privacy_version ?? "0.0.0", state.product.privacy_version ?? "0.0.0"))
			privacy = false;
		if (terms && privacy)
			return props.onAccepted();
	}

	let name = props.custom?.name ?? state.product.name ?? "";
	let logo = props.custom?.logo ?? state.product.logo ?? "";
	let background = props.custom?.background ?? state.product.background;

	return (
		<div
			style={{
				width: '100%',
				background: background ? `url(${background})` : "",
				backgroundSize: props.custom?.background?.size ?? "cover",
				backgroundAttachment: props.custom?.background?.attachment ?? "fixed"
			}}
		>
			<div className={`container ${Styles.nsa_consent_page_container}`}>
				<div className={Styles.nsa_consent_page_logo}>
					<img width={256} height={256} src={logo} alt={`${name} Logo`} />
				</div>
				<div>
					<h1 className={Styles.nsa_consent_page_title}>{name}</h1>
				</div>
				<NSSpace size={NSSpaceSizeType.SMALL} />
				<p
					style={{ margin: "0px", maxWidth: NSBox.width.double }}
				>
					Before continuing, please read and accept our <a href={state.product.terms ?? ""}>Terms of Conditions</a> and <a href={state.product.privacy ?? ""}>Privacy Policy</a>. These documents are designed to help you understand what you can expect from us as a service provider and what we expect from you as a user. They explain how we collect, use, and protect your personal information, as well as the rules and guidelines for using our platform. Your privacy and trust are very important to us, and we are committed to being transparent about how your data is handled. By checking the box below, you confirm that you have read, understood, and agreed to these terms.
				</p>
				<NSBoxBoolean
					ref={NSBoxBoolean_Agree}
					required={false}
					title=''
					style={{ width: "max-content" }}
					onChanged={(e) => { setState(prev => { return { ...prev, accepted: e.getValue() ?? false }; }); }}
				>
					<p
						style={{ margin: "0px" }}
					>
						I have read and accpeted the <a href={state.product.terms ?? ""}>Terms of Conditions</a> and <a href={state.product.privacy ?? ""}>Privacy Policy</a>
					</p>
				</NSBoxBoolean>
				<NSSpace size={NSSpaceSizeType.SMALL} />
				<NSButtonBlue
					title="Accept"
					onClick={{
						action: (onFinished) =>
						{
							props.account.server.consent.Set({ product_id: props.product_id }).then((consent) =>
							{
								cache_consent.set(consent);
								setState(prev => { return { ...prev, consent } });
								onFinished();
							}).catch(onFinished);
						}
					}}
					disabled={!state.accepted}
				/>
			</div>
		</div>
	);
}